import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Analytics, logEvent } from '@angular/fire/analytics';

export enum LogLevel {
  INFO,
  WARN,
  ERROR,
}

export interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  context?: any;
}

const MAX_LOG_ENTRIES = 10; // Limit the number of log entries
const MAX_CONTEXT_LENGTH = 200; // Limit the length of context string

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private logs: LogEntry[] = [];
  private logsSubject = new BehaviorSubject<LogEntry[]>([]);
  private _analytics: Analytics | null = null;

  constructor(private injector: Injector) {
    // Load logs from session storage on service initialization
    const storedLogs = sessionStorage.getItem('app_logs');
    if (storedLogs) {
      this.logs = JSON.parse(storedLogs);
      this.logsSubject.next(this.logs);
    }
  }

  // Lazily load the Analytics service to break the circular dependency
  private get analytics(): Analytics {
    if (!this._analytics) {
      this._analytics = this.injector.get(Analytics);
    }
    return this._analytics;
  }

  log(level: LogLevel, message: string, context?: any) {
    let serializedContext: string | undefined;
    if (context) {
      try {
        serializedContext = JSON.stringify(context);
        if (serializedContext.length > MAX_CONTEXT_LENGTH) {
          serializedContext = serializedContext.substring(0, MAX_CONTEXT_LENGTH) + '...';
        }
      } catch (e) {
        serializedContext = '(Error serializing context)';
      }
    }

    const logEntry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      context: serializedContext,
    };

    if (this.logs.length >= MAX_LOG_ENTRIES) {
      this.logs.shift(); // Remove the oldest entry
    }
    this.logs.push(logEntry);
    this.logsSubject.next(this.logs);
    this.persistLogs();
  }

  info(message: string, context?: any) {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: any) {
    this.log(LogLevel.WARN, message, context);
  }

  error(message: string, context?: any) {
    this.log(LogLevel.ERROR, message, context);
  }

  getLogs(): Observable<LogEntry[]> {
    return this.logsSubject.asObservable();
  }

  logEvent(eventName: string, eventParams: { [key: string]: any }) {
    logEvent(this.analytics, eventName, eventParams);
  }

  private persistLogs() {
    // Persist logs to session storage
    sessionStorage.setItem('app_logs', JSON.stringify(this.logs));
  }
}
